import { supabase } from '../lib/supabaseClient';
import type {
  Complaint,
  ComplaintStatus,
  Consent,
  ConsentType,
  Customer,
  CustomerIdentity,
  CustomerIdentityType,
  CustomerMergeRequest,
  Feedback,
  FeedbackStatus,
  UUID,
} from '../types/domain';

/**
 * CRM Foundation (FASE 11, migration 0048) — reads and writes wired to the
 * real backend. Every write below calls a permission-gated `_checked` RPC —
 * never a direct `.insert()`/`.update()`.
 *
 * Loyalty boundary (V5, non-negotiable): this module is customer identity /
 * consent / feedback / complaint ONLY. No points/loyalty-ledger/rewards/
 * coupon/POS-transaction-linkage functions exist here — those remain
 * DEFERRED_WITH_POS (no loyalty_* schema exists in this project at all).
 *
 * Merge boundary (V5, non-negotiable): there is no "auto-merge" function.
 * fn_add_customer_identity_checked only ever *flags* a PENDING_REVIEW
 * customer_merge_requests row when two customers claim the same identity —
 * it never merges automatically. Only decideCustomerMerge (below), gated on
 * customer_merge_request.decide and server-enforced maker-checker (the
 * decider can never be the same actor who triggered the flag), can approve
 * or reject it.
 */

// ---------------------------------------------------------------------------
// Customers & identities
// ---------------------------------------------------------------------------

export async function listCustomers(): Promise<Customer[]> {
  const { data, error } = await supabase
    .from('customers')
    .select('customer_id, tenant_id, customer_no, name, primary_branch_id, status, merged_into_customer_id, created_at, created_by')
    .order('created_at', { ascending: false });
  if (error) throw new Error(`listCustomers failed: ${error.message}`);
  return (data ?? []) as Customer[];
}

export async function getCustomer(customerId: UUID): Promise<Customer | null> {
  const { data, error } = await supabase
    .from('customers')
    .select('customer_id, tenant_id, customer_no, name, primary_branch_id, status, merged_into_customer_id, created_at, created_by')
    .eq('customer_id', customerId)
    .maybeSingle();
  if (error) throw new Error(`getCustomer failed: ${error.message}`);
  return data as Customer | null;
}

export async function listCustomerIdentities(customerId: UUID): Promise<CustomerIdentity[]> {
  const { data, error } = await supabase
    .from('customer_identities')
    .select('identity_id, tenant_id, customer_id, identity_type, identity_value, created_at, created_by')
    .eq('customer_id', customerId)
    .order('created_at', { ascending: false });
  if (error) throw new Error(`listCustomerIdentities failed: ${error.message}`);
  return (data ?? []) as CustomerIdentity[];
}

/** branchId is used only for customer_no numbering/traceability (fn_next_document_number requires a real branch) — customer identity itself is tenant-wide, not branch-scoped (primary_branch_id stays nullable). */
export async function createCustomer(branchId: UUID, name: string): Promise<UUID> {
  const { data, error } = await supabase.rpc('fn_create_customer_checked', {
    p_branch_id: branchId,
    p_name: name,
  });
  if (error) throw new Error(`fn_create_customer_checked failed: ${error.message}`);
  return data as UUID;
}

/** Blocked server-side once the customer is MERGED — edit the survivor instead. */
export async function updateCustomer(customerId: UUID, name: string, primaryBranchId: UUID | null): Promise<void> {
  const { error } = await supabase.rpc('fn_update_customer_checked', {
    p_customer_id: customerId,
    p_name: name,
    p_primary_branch_id: primaryBranchId,
  });
  if (error) throw new Error(`fn_update_customer_checked failed: ${error.message}`);
}

/**
 * Adds an identity claim. Returns null when the identity is new or already
 * belongs to this same customer. Returns a customer_merge_requests id when
 * the identity is already claimed by a DIFFERENT customer — this is a FLAG,
 * not a merge; the two customers remain fully separate and ACTIVE until an
 * authorized decideCustomerMerge call resolves it.
 */
export async function addCustomerIdentity(customerId: UUID, identityType: CustomerIdentityType, identityValue: string): Promise<UUID | null> {
  const { data, error } = await supabase.rpc('fn_add_customer_identity_checked', {
    p_customer_id: customerId,
    p_identity_type: identityType,
    p_identity_value: identityValue,
  });
  if (error) throw new Error(`fn_add_customer_identity_checked failed: ${error.message}`);
  return (data as UUID | null) ?? null;
}

// ---------------------------------------------------------------------------
// Controlled customer merge (never automatic)
// ---------------------------------------------------------------------------

export async function listCustomerMergeRequests(): Promise<CustomerMergeRequest[]> {
  const { data, error } = await supabase
    .from('customer_merge_requests')
    .select(
      'merge_request_id, tenant_id, customer_a_id, customer_b_id, conflicting_identity_type, conflicting_identity_value, status, requested_by, decided_by, decided_at, survivor_customer_id, created_at'
    )
    .order('created_at', { ascending: false });
  if (error) throw new Error(`listCustomerMergeRequests failed: ${error.message}`);
  return (data ?? []) as CustomerMergeRequest[];
}

/**
 * The ONLY function that can resolve a merge request. Server-enforced
 * maker-checker: rejects if the deciding actor is the same user who
 * triggered the original flag. On approve, survivorCustomerId must be one of
 * the two conflicting customers; the other is marked MERGED (never deleted)
 * with merged_into_customer_id set, and its identities/feedback/complaints/
 * consents are reassigned to the survivor — no orphaned interaction history.
 */
export async function decideCustomerMerge(mergeRequestId: UUID, approve: boolean, survivorCustomerId: UUID | null): Promise<void> {
  const { error } = await supabase.rpc('fn_decide_customer_merge_checked', {
    p_merge_request_id: mergeRequestId,
    p_approve: approve,
    p_survivor_customer_id: survivorCustomerId,
  });
  if (error) throw new Error(`fn_decide_customer_merge_checked failed: ${error.message}`);
}

// ---------------------------------------------------------------------------
// Consent (opt-out enforced, never silently re-enabled)
// ---------------------------------------------------------------------------

export async function listConsents(customerId: UUID): Promise<Consent[]> {
  const { data, error } = await supabase
    .from('consents')
    .select('consent_id, tenant_id, customer_id, consent_type, granted, changed_at, changed_by')
    .eq('customer_id', customerId)
    .order('changed_at', { ascending: false });
  if (error) throw new Error(`listConsents failed: ${error.message}`);
  return (data ?? []) as Consent[];
}

/** Append-only: every call inserts a new history row (changed_at/changed_by), never edits a prior one. The current effective state is the latest row per consent_type. */
export async function setConsent(customerId: UUID, consentType: ConsentType, granted: boolean): Promise<void> {
  const { error } = await supabase.rpc('fn_set_consent_checked', {
    p_customer_id: customerId,
    p_consent_type: consentType,
    p_granted: granted,
  });
  if (error) throw new Error(`fn_set_consent_checked failed: ${error.message}`);
}

// ---------------------------------------------------------------------------
// Feedback
// ---------------------------------------------------------------------------

export async function listFeedback(): Promise<Feedback[]> {
  const { data, error } = await supabase
    .from('feedback')
    .select('feedback_id, tenant_id, branch_id, customer_id, channel, rating, comment, status, created_at, created_by')
    .order('created_at', { ascending: false });
  if (error) throw new Error(`listFeedback failed: ${error.message}`);
  return (data ?? []) as Feedback[];
}

/**
 * idempotencyKey (migration 0049): the caller must generate this ONCE per
 * logical submission (e.g. once when the form opens, reused across a
 * double-click or a network-retry resubmit of the SAME form state) — never
 * a fresh uuid per call. The server enforces `unique (tenant_id,
 * idempotency_key)`: a retry with the same key deterministically resolves
 * to the already-created canonical row (no duplicate), while a genuinely
 * new key always creates a genuinely new record, even with identical
 * content. Dedup is by key only — never by message text/customer/timestamp.
 */
export async function logFeedback(
  branchId: UUID,
  customerId: UUID,
  channel: string,
  rating: number | null,
  comment: string | null,
  idempotencyKey: UUID
): Promise<UUID> {
  const { data, error } = await supabase.rpc('fn_log_feedback_checked', {
    p_branch_id: branchId,
    p_customer_id: customerId,
    p_channel: channel,
    p_rating: rating,
    p_comment: comment,
    p_idempotency_key: idempotencyKey,
  });
  if (error) throw new Error(`fn_log_feedback_checked failed: ${error.message}`);
  return data as UUID;
}

/** REVIEWED/RESOLVED only; blocked once already RESOLVED (terminal). */
export async function updateFeedbackStatus(feedbackId: UUID, status: FeedbackStatus): Promise<void> {
  const { error } = await supabase.rpc('fn_update_feedback_status_checked', {
    p_feedback_id: feedbackId,
    p_status: status,
  });
  if (error) throw new Error(`fn_update_feedback_status_checked failed: ${error.message}`);
}

// ---------------------------------------------------------------------------
// Complaints (full E2E: create -> review -> resolution -> audit/history)
// ---------------------------------------------------------------------------

export async function listComplaints(): Promise<Complaint[]> {
  const { data, error } = await supabase
    .from('complaints')
    .select('complaint_id, tenant_id, branch_id, customer_id, subject, description, status, created_at, created_by, resolved_at, resolved_by')
    .order('created_at', { ascending: false });
  if (error) throw new Error(`listComplaints failed: ${error.message}`);
  return (data ?? []) as Complaint[];
}

/** idempotencyKey: see the note on logFeedback above — same contract, same server-side `unique (tenant_id, idempotency_key)` guarantee. */
export async function logComplaint(branchId: UUID, customerId: UUID, subject: string, description: string, idempotencyKey: UUID): Promise<UUID> {
  const { data, error } = await supabase.rpc('fn_log_complaint_checked', {
    p_branch_id: branchId,
    p_customer_id: customerId,
    p_subject: subject,
    p_description: description,
    p_idempotency_key: idempotencyKey,
  });
  if (error) throw new Error(`fn_log_complaint_checked failed: ${error.message}`);
  return data as UUID;
}

/** IN_PROGRESS/ESCALATED only — resolution goes through resolveComplaint, not this. Blocked once already RESOLVED (terminal). */
export async function updateComplaintStatus(complaintId: UUID, status: Exclude<ComplaintStatus, 'NEW' | 'RESOLVED'>): Promise<void> {
  const { error } = await supabase.rpc('fn_update_complaint_status_checked', {
    p_complaint_id: complaintId,
    p_status: status,
  });
  if (error) throw new Error(`fn_update_complaint_status_checked failed: ${error.message}`);
}

export async function resolveComplaint(complaintId: UUID): Promise<void> {
  const { error } = await supabase.rpc('fn_resolve_complaint_checked', { p_complaint_id: complaintId });
  if (error) throw new Error(`fn_resolve_complaint_checked failed: ${error.message}`);
}
